// ─── useCrediti — hook per gestione crediti utente ────────────────────────────
// Espone:
//   balance       → saldo crediti corrente (number | null se loading)
//   loading       → true durante il primo fetch
//   plan          → 'free' | 'base' | 'premium'
//   transactions  → ultime N transazioni (per pagina Account)
//   consume       → async (amount, reason, metadata?) → { success, balanceAfter, error? }
//   refresh       → ricarica saldo dal server
//
// In "modalità demo" (SUPABASE_ENABLED=false): consume ritorna sempre success
// senza fare alcunché, balance resta null. Nessun blocco — flusso esistente OK.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, SUPABASE_ENABLED } from './supabase';

export const COSTI_CREDITI = {
  AI_FIELD: 1,           // ogni "Genera con AI" / "Migliora con AI" su un campo
  VERIFICA_COERENZA: 3,  // pulsante "Verifica coerenza"
  DOCUMENTO_UPLOAD: 1,   // analisi AI delle sezioni rilevanti al caricamento documento
};

export const SOGLIA_AVVISO = 5;  // mostra warning quando ≤ 5 crediti

// ─── GATING TRIAL (plan = 'free') ─────────────────────────────────────────────
// L'utente in trial può accedere solo alle prime sezioni del PEI, e nella
// sezione 4 (Osservazioni) può compilare solo la dimensione A.
// Le altre sezioni restano visibili (per non perdere dati già inseriti e per
// invogliare all'upgrade) ma in sola lettura: niente generazione AI, niente
// "Correggi", niente "Verifica coerenza", niente upload documenti.
//
// IMPORTANTE: il gate è legato a `plan === 'free'`, NON al saldo crediti.
// In questo modo, appena l'utente passa a un piano pagante, sblocca tutto
// indipendentemente da quanti crediti gli restano.
export const SEZIONI_TRIAL_SBLOCCATE = new Set(['glo', 's1', 's2', 's3', 's4']);
export const DIMENSIONE_TRIAL_SBLOCCATA = 'A';

export function isPianoTrial(plan) {
  return plan === 'free';
}

export function isSezioneBloccataTrial(sezId, plan) {
  if (!isPianoTrial(plan)) return false;
  return !SEZIONI_TRIAL_SBLOCCATE.has(sezId);
}

export function isDimensioneBloccataTrial(dimId, plan) {
  if (!isPianoTrial(plan)) return false;
  return dimId !== DIMENSIONE_TRIAL_SBLOCCATA;
}

export function useCrediti(user) {
  const [balance, setBalance]         = useState(null);
  const [plan, setPlan]               = useState('free');
  const [band, setBand]               = useState(null); // fascia di scuola: inf_pri | sec1 | sec2 | null
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null); // diagnostic
  const [transactions, setTrans]      = useState([]);
  const channelRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    if (!SUPABASE_ENABLED || !user?.id) { setLoading(false); return; }
    const { data, error: err } = await supabase
      .from('profiles')
      .select('credits_balance, plan, band')
      .eq('id', user.id)
      .maybeSingle();   // ← single() lancia errore se 0 righe; maybeSingle è più tollerante
    if (err) {
      // eslint-disable-next-line no-console
      console.error('[useCrediti] errore fetch profilo:', err);
      setError(`Errore fetch profilo: ${err.message} (codice ${err.code || 'n/a'})`);
    } else if (!data) {
      // eslint-disable-next-line no-console
      console.error('[useCrediti] nessun profilo trovato per user.id', user.id);
      setError('Profilo utente non trovato in database. Probabilmente il trigger di signup non è stato eseguito (migration 0001/0003 non applicata?).');
    } else {
      setBalance(data.credits_balance);
      setPlan(data.plan);
      setBand(data.band ?? null);
      setError(null);
    }
    setLoading(false);
  }, [user?.id]);

  const fetchTransactions = useCallback(async () => {
    if (!SUPABASE_ENABLED || !user?.id) return;
    const { data } = await supabase
      .from('credit_transactions')
      .select('id, delta, reason, metadata, balance_after, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);
    setTrans(data || []);
  }, [user?.id]);

  // Initial load + realtime subscription per ricevere update istantanei
  useEffect(() => {
    if (!SUPABASE_ENABLED || !user?.id) { setLoading(false); return; }
    fetchProfile();
    fetchTransactions();

    const channel = supabase
      .channel(`profile-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          if (payload.new) {
            setBalance(payload.new.credits_balance);
            setPlan(payload.new.plan);
            setBand(payload.new.band ?? null);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'credit_transactions', filter: `user_id=eq.${user.id}` },
        () => { fetchTransactions(); }
      )
      .subscribe();
    channelRef.current = channel;

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id, fetchProfile, fetchTransactions]);

  // Consuma N crediti per una operazione (es. campo AI). Atomico server-side.
  const consume = useCallback(async (amount, reason, metadata) => {
    if (!SUPABASE_ENABLED || !user?.id) {
      // Modalità demo: nessun controllo crediti.
      return { success: true, balanceAfter: null, demo: true };
    }
    const { data, error } = await supabase.rpc('consume_credits', {
      p_user_id:  user.id,
      p_amount:   amount,
      p_reason:   reason,
      p_metadata: metadata || null,
    });
    if (error) return { success: false, error: error.message };
    const row = Array.isArray(data) ? data[0] : data;
    if (row?.success) {
      setBalance(row.balance_after);
      return { success: true, balanceAfter: row.balance_after };
    }
    return { success: false, error: row?.error_msg || 'unknown' };
  }, [user?.id]);

  return {
    balance,
    plan,
    band,
    loading,
    error,
    transactions,
    consume,
    refresh: fetchProfile,
    refreshTransactions: fetchTransactions,
  };
}
